import { API_ENDPOINTS } from '@/api/endpoints.constant';
import { diagnosticsLogStorage, PendingLogRecord } from '@/store/idbStorage/diagnosticsLogStorage';
import { DIAGNOSTICS_CONSTANTS } from '@/utils/diagnostics/diagnostics.constants';
import { ChunkedLogPart, normalizeLogRequestPayload } from '@/utils/diagnostics/logChunker.util';

type DrainContext = {
  apiUrl: string;
  token: string;
};

let isPaused = false;
let isDraining = false;
let drainTimer: ReturnType<typeof setTimeout> | null = null;
let drainContext: DrainContext | null = null;

function clearDrainTimer(): void {
  if (drainTimer !== null) {
    clearTimeout(drainTimer);
    drainTimer = null;
  }
}

function scheduleDrainInternal(delayMs: number): void {
  try {
    clearDrainTimer();
    drainTimer = setTimeout(
      () => {
        void drainPendingLogs().catch((error) => {
          console.error('[diagnostics] drain timer failed', error);
        });
      },
      Math.max(0, delayMs)
    );
  } catch (error) {
    console.error('[diagnostics] scheduleDrainInternal failed', error);
  }
}

function computeRetryDelay(attemptCount: number): number {
  const exponential = DIAGNOSTICS_CONSTANTS.PENDING_LOGS.BASE_RETRY_MS * 2 ** attemptCount;
  const jitter = Math.floor(Math.random() * 250);
  return Math.min(exponential + jitter, DIAGNOSTICS_CONSTANTS.PENDING_LOGS.MAX_RETRY_MS);
}

function isRetryableStatus(status: number): boolean {
  return status === 408 || status === 429 || status >= 500;
}

function isAuthStatus(status: number): boolean {
  return status === 401 || status === 403;
}

async function sendPendingRecord(
  record: PendingLogRecord,
  context: DrainContext
): Promise<'sent' | 'retry' | 'drop'> {
  const url = new URL(`${context.apiUrl}/${API_ENDPOINTS.LOGS.LOGS}`);
  try {
    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${context.token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(normalizeLogRequestPayload(record.payload)),
    });
    if (response.ok) {
      return 'sent';
    }
    if (isAuthStatus(response.status)) {
      return 'retry';
    }
    if (isRetryableStatus(response.status)) {
      return 'retry';
    }
    console.warn('[diagnostics] dropping invalid log part', {
      partId: record.partId,
      status: response.status,
    });
    return 'drop';
  } catch {
    return 'retry';
  }
}

async function enforcePendingLimits(): Promise<void> {
  const records = await diagnosticsLogStorage.getAllPendingLogs();
  const now = Date.now();
  const maxAge = DIAGNOSTICS_CONSTANTS.PENDING_LOGS.MAX_AGE_MS;
  const sorted = [...records].sort((left, right) => left.createdAt.localeCompare(right.createdAt));
  let totalBytes = sorted.reduce((sum, record) => sum + record.byteSize, 0);
  const survivors: PendingLogRecord[] = [];
  for (const record of sorted) {
    const ageMs = now - Date.parse(record.createdAt);
    if (ageMs > maxAge) {
      await diagnosticsLogStorage.deletePendingLog(record.partId);
      continue;
    }
    if (record.attemptCount >= DIAGNOSTICS_CONSTANTS.PENDING_LOGS.MAX_ATTEMPTS) {
      await diagnosticsLogStorage.deletePendingLog(record.partId);
      continue;
    }
    survivors.push(record);
  }
  const levelPriority = (record: PendingLogRecord): number => (record.level === 'error' ? 1 : 0);
  while (survivors.length > DIAGNOSTICS_CONSTANTS.PENDING_LOGS.MAX_PARTS) {
    const removableIndex = survivors.findIndex((record) => levelPriority(record) === 0);
    const index = removableIndex >= 0 ? removableIndex : 0;
    const [removed] = survivors.splice(index, 1);
    await diagnosticsLogStorage.deletePendingLog(removed.partId);
    totalBytes -= removed.byteSize;
  }
  while (totalBytes > DIAGNOSTICS_CONSTANTS.PENDING_LOGS.MAX_BYTES && survivors.length > 0) {
    const removableIndex = survivors.findIndex((record) => levelPriority(record) === 0);
    const index = removableIndex >= 0 ? removableIndex : 0;
    const [removed] = survivors.splice(index, 1);
    await diagnosticsLogStorage.deletePendingLog(removed.partId);
    totalBytes -= removed.byteSize;
  }
}

async function claimDueRecord(nowIso: string): Promise<PendingLogRecord | null> {
  const records = await diagnosticsLogStorage.getAllPendingLogs();
  const due = records
    .filter((record) => record.nextAttemptAt <= nowIso)
    .filter((record) => !record.leaseUntil || record.leaseUntil <= nowIso)
    .sort((left, right) => left.nextAttemptAt.localeCompare(right.nextAttemptAt));
  if (due.length === 0) {
    return null;
  }
  const record = due[0];
  const leaseUntil = new Date(
    Date.now() + DIAGNOSTICS_CONSTANTS.PENDING_LOGS.LEASE_MS
  ).toISOString();
  const claimed: PendingLogRecord = { ...record, leaseUntil };
  await diagnosticsLogStorage.updatePendingLog(claimed);
  return claimed;
}

export const logOutbox = {
  setPaused(paused: boolean): void {
    try {
      isPaused = paused;
      if (!paused) {
        scheduleDrainInternal(0);
      }
    } catch (error) {
      console.error('[diagnostics] setPaused failed', error);
    }
  },

  setDrainContext(context: DrainContext | null): void {
    try {
      drainContext = context;
      if (context) {
        scheduleDrainInternal(0);
      }
    } catch (error) {
      console.error('[diagnostics] setDrainContext failed', error);
    }
  },

  async enqueueParts(
    parts: ChunkedLogPart[],
    meta: { sessionId: string; syncId: string }
  ): Promise<void> {
    try {
      await diagnosticsLogStorage.enqueuePendingParts(parts, meta);
      await enforcePendingLimits();
      scheduleDrainInternal(0);
    } catch (error) {
      console.error('[diagnostics] failed to enqueue log parts', error);
    }
  },

  scheduleDrain(delayMs = 0): void {
    scheduleDrainInternal(delayMs);
  },
};

export async function drainPendingLogs(): Promise<void> {
  if (isDraining || isPaused || !drainContext?.apiUrl || !drainContext.token) {
    return;
  }
  isDraining = true;
  let nextDelayMs: number | null = null;
  try {
    await enforcePendingLimits();
    const nowIso = new Date().toISOString();
    const record = await claimDueRecord(nowIso);
    if (!record) {
      return;
    }
    const result = await sendPendingRecord(record, drainContext);
    if (result === 'sent') {
      await diagnosticsLogStorage.deletePendingLog(record.partId);
      scheduleDrainInternal(0);
      return;
    }
    if (result === 'drop') {
      await diagnosticsLogStorage.deletePendingLog(record.partId);
      scheduleDrainInternal(0);
      return;
    }
    const attemptCount = record.attemptCount + 1;
    if (attemptCount >= DIAGNOSTICS_CONSTANTS.PENDING_LOGS.MAX_ATTEMPTS) {
      await diagnosticsLogStorage.deletePendingLog(record.partId);
      scheduleDrainInternal(0);
      return;
    }
    const delayMs = computeRetryDelay(attemptCount);
    nextDelayMs = delayMs;
    const updated: PendingLogRecord = {
      ...record,
      attemptCount,
      leaseUntil: null,
      nextAttemptAt: new Date(Date.now() + delayMs).toISOString(),
    };
    await diagnosticsLogStorage.updatePendingLog(updated);
  } catch (error) {
    console.error('[diagnostics] drain failed', error);
    nextDelayMs = DIAGNOSTICS_CONSTANTS.PENDING_LOGS.BASE_RETRY_MS;
  } finally {
    isDraining = false;
    if (nextDelayMs !== null) {
      scheduleDrainInternal(nextDelayMs);
    }
  }
}

if (typeof self !== 'undefined' && 'addEventListener' in self) {
  try {
    self.addEventListener('online', () => {
      try {
        scheduleDrainInternal(0);
      } catch (error) {
        console.error('[diagnostics] online drain failed', error);
      }
    });
  } catch (error) {
    console.error('[diagnostics] failed to register online listener', error);
  }
}
